/**
 * Configuração centralizada de planos e funcionalidades
 * 
 * Este arquivo define os planos disponíveis e suas permissões de acesso.
 * Modificar este arquivo para adicionar/remover funcionalidades de cada plano.
 */

export type SubscriptionPlan = 'free' | 'basic' | 'premium' | 'enterprise';

export type SubscriptionStatus = 'active' | 'late' | 'canceled' | 'trial';

/**
 * Features disponíveis no sistema
 */
export type Feature = 
  | 'dashboard'           // Visão Geral
  | 'transactions'        // Transações
  | 'goals'              // Metas
  | 'news'               // Fique por Dentro
  | 'reports'            // Relatórios
  | 'whatsapp-enterprise' // Suporte WhatsApp Enterprise
  | 'agenda'             // Agenda
  | 'ai-assistant'       // Assistente com IA
  | 'voice-agent';       // Agente de Voz

/**
 * Configuração de funcionalidades por plano
 * 
 * IMPORTANTE: Cada plano herda as funcionalidades dos planos inferiores
 * O array deve conter apenas as funcionalidades ADICIONAIS de cada plano
 */
export const PLAN_FEATURES: Record<SubscriptionPlan, Feature[]> = {
  // Plano Gratuito - Funcionalidades básicas
  free: [
    'dashboard',
    'transactions',
    'goals',
    'news',
  ],
  
  // 1º Plano Pago - Adiciona relatórios
  basic: [
    'reports',
  ],
  
  // 2º Plano Pago - Adiciona agenda e assistente com IA
  premium: [
    'agenda',
    'ai-assistant',
  ],
  
  // 3º Plano Pago - Adiciona agente de voz
  enterprise: [
    'voice-agent',
    'whatsapp-enterprise',
  ],
};

/**
 * Mapeamento de rotas para features
 * Usado para validar acesso às páginas
 */
export const ROUTE_FEATURE_MAP: Record<string, Feature> = {
  '/dashboard': 'dashboard',
  '/transacoes': 'transactions',
  '/metas': 'goals',
  '/fique-por-dentro': 'news',
  '/relatorios': 'reports',
  '/whatsapp': 'whatsapp-enterprise',
  '/agenda': 'agenda',
  '/assistente-ia': 'ai-assistant',
  '/voz': 'voice-agent',
};

/**
 * Labels amigáveis para os planos
 */
export const PLAN_LABELS: Record<SubscriptionPlan, string> = {
  free: 'Gratuito',
  basic: 'Básico',
  premium: 'Premium',
  enterprise: 'Enterprise',
};

/**
 * Labels amigáveis para as funcionalidades
 */
export const FEATURE_LABELS: Record<Feature, string> = {
  dashboard: 'Visão Geral',
  transactions: 'Transações',
  goals: 'Metas',
  news: 'Fique por Dentro',
  reports: 'Relatórios',
  'whatsapp-enterprise': 'Suporte WhatsApp Enterprise',
  agenda: 'Agenda',
  'ai-assistant': 'Assistente com IA',
  'voice-agent': 'Agente de Voz',
};

/**
 * Hierarquia de planos (ordem crescente)
 */
export const PLAN_HIERARCHY: SubscriptionPlan[] = ['free', 'basic', 'premium', 'enterprise'];

/**
 * Retorna todas as funcionalidades disponíveis para um plano
 * Inclui funcionalidades herdadas de planos inferiores
 */
export function getAvailableFeatures(plan: SubscriptionPlan): Feature[] {
  const planIndex = PLAN_HIERARCHY.indexOf(plan);
  if (planIndex === -1) return PLAN_FEATURES.free;
  
  // Acumula features de todos os planos até o plano atual
  const features: Feature[] = [];
  for (let i = 0; i <= planIndex; i++) {
    features.push(...PLAN_FEATURES[PLAN_HIERARCHY[i]]);
  }
  
  return features;
}

/**
 * Verifica se um plano tem acesso a uma funcionalidade
 */
export function hasFeatureAccess(plan: SubscriptionPlan, feature: Feature): boolean {
  const availableFeatures = getAvailableFeatures(plan);
  return availableFeatures.includes(feature);
}

/**
 * Retorna o menor plano que possui acesso a uma funcionalidade
 */
export function getMinimumPlanForFeature(feature: Feature): SubscriptionPlan {
  for (const plan of PLAN_HIERARCHY) {
    if (hasFeatureAccess(plan, feature)) {
      return plan;
    }
  }
  return 'enterprise'; // Fallback
}

/**
 * Verifica se o status da assinatura permite acesso
 * Usuários com pagamento atrasado, cancelado ou com prazo expirado têm acesso limitado
 */
export function isSubscriptionActive(status: SubscriptionStatus, endsAt?: string | null): boolean {
  if (status !== 'active' && status !== 'trial') return false;
  if (endsAt) {
    const expireTime = Date.parse(endsAt);
    if (!isNaN(expireTime) && expireTime < Date.now()) {
      return false;
    }
  }
  return true;
}

/**
 * Retorna mensagem apropriada para status de assinatura inativo
 */
export function getInactiveSubscriptionMessage(status: SubscriptionStatus): string {
  switch (status) {
    case 'late':
      return 'Seu pagamento está atrasado. Regularize para continuar usando todas as funcionalidades.';
    case 'canceled':
      return 'Sua assinatura foi cancelada. Reative para ter acesso às funcionalidades premium.';
    default:
      return 'Assinatura inativa.';
  }
}
